import { theme, FONT_RUNE } from "./theme";
import { useGame } from "./store";
import type { Piece, Side } from "../engine";

/**
 * Vertical column of captured pieces alongside the board, in capture order
 * (oldest at top, most recent at bottom). One strip per side:
 *   - left strip: pieces captured BY white (i.e. black pieces that white took)
 *   - right strip: pieces captured BY black
 */
export function CapturesStrip({ owner }: { owner: Side }) {
  const captured = useGame((s) => s.game.captured[owner]);
  const totalValue = captured.reduce((acc, p) => acc + p.value, 0);

  return (
    <div
      style={{
        width: 44,
        flexShrink: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
        padding: "8px 4px",
        background: "rgba(13, 10, 7, 0.55)",
        borderRight: owner === "white" ? `1px solid ${theme.boardEdge}` : "none",
        borderLeft: owner === "black" ? `1px solid ${theme.boardEdge}` : "none",
        overflowY: "auto",
      }}
    >
      <div
        className="rune"
        style={{
          fontSize: 10,
          letterSpacing: 1,
          color: theme.boardLabel,
          textAlign: "center",
          marginBottom: 2,
        }}
        title={`Pieces captured by ${owner === "white" ? "White" : "Black"}`}
      >
        {owner === "white" ? "WHITE" : "BLACK"}
      </div>
      <div
        className="rune"
        style={{
          fontSize: 13,
          color: theme.accentSoft,
          marginBottom: 4,
        }}
      >
        {totalValue}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1 }}>
        {captured.map((p, i) => (
          <CapturedChip key={`${p.id}-${i}`} piece={p} />
        ))}
      </div>
    </div>
  );
}

function CapturedChip({ piece }: { piece: Piece }) {
  const fill = piece.side === "white" ? theme.whiteFill : theme.blackFill;
  const stroke = piece.side === "white" ? theme.whiteEdge : theme.blackEdge;
  const text = piece.side === "white" ? theme.whiteNumber : theme.blackNumber;

  // Mini glyph by shape. 24×24 box (~15% smaller than the previous 28).
  const box = 24;
  const inner = 19;

  let glyph: React.ReactNode;
  if (piece.shape === "round") {
    glyph = (
      <svg viewBox={`0 0 ${box} ${box}`} width={box} height={box} aria-hidden>
        <circle cx={box / 2} cy={box / 2} r={inner / 2} fill={fill} stroke={stroke} strokeWidth={1} />
      </svg>
    );
  } else if (piece.shape === "triangle") {
    const h = inner * 0.866;
    const cx = box / 2;
    const cy = box / 2;
    const up =
      piece.side === "white"
        ? `${cx},${cy - h * 0.55} ${cx + inner / 2},${cy + h * 0.45} ${cx - inner / 2},${cy + h * 0.45}`
        : `${cx},${cy + h * 0.55} ${cx + inner / 2},${cy - h * 0.45} ${cx - inner / 2},${cy - h * 0.45}`;
    glyph = (
      <svg viewBox={`0 0 ${box} ${box}`} width={box} height={box} aria-hidden>
        <polygon points={up} fill={fill} stroke={stroke} strokeWidth={1} />
      </svg>
    );
  } else if (piece.shape === "square") {
    glyph = (
      <svg viewBox={`0 0 ${box} ${box}`} width={box} height={box} aria-hidden>
        <rect
          x={(box - inner) / 2}
          y={(box - inner) / 2}
          width={inner}
          height={inner}
          fill={fill}
          stroke={stroke}
          strokeWidth={1}
        />
      </svg>
    );
  } else {
    // pyramid
    const cx = box / 2;
    const cy = box / 2;
    const half = inner / 2;
    glyph = (
      <svg viewBox={`0 0 ${box} ${box}`} width={box} height={box} aria-hidden>
        <rect
          x={cx - half * 0.85}
          y={cy - half * 0.15}
          width={half * 1.7}
          height={half * 0.85}
          fill={fill}
          stroke={stroke}
          strokeWidth={1}
        />
        <polygon
          points={`${cx},${cy - half} ${cx + half * 0.85},${cy - half * 0.15} ${cx - half * 0.85},${cy - half * 0.15}`}
          fill={fill}
          stroke={stroke}
          strokeWidth={1}
        />
      </svg>
    );
  }

  return (
    <div
      title={`${piece.shape} ${piece.value}`}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: box,
        height: box,
      }}
    >
      {glyph}
      <span
        style={{
          position: "absolute",
          fontFamily: FONT_RUNE,
          fontSize: 9,
          color: text,
          pointerEvents: "none",
        }}
      >
        {piece.value}
      </span>
    </div>
  );
}
